import React, { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from 'react-query';
import { WizardStep, stepForField } from './consts';
import { OrderFormProps, ProductsTableItem } from '../OrderForm/types';
import { OrderFormValues } from '../../types';
import { useFeesProfileData } from '../OrderForm/hooks';
import useGeocodeAddress from '../../../../../hooks/useGeocodeAddress';
import apiRoutes from '../../../../../setup/consts/apiRoutes';
import { GetProductDetailsResponse } from '../../../../../setup/apiTypes/warehouse';
import { GetCustomerResponse } from '../../../../../setup/apiTypes/customers';
import WizardStepper from './WizardStepper';
import WizardFooter from './WizardFooter';
import StepStoreCustomer from './steps/StepStoreCustomer';
import StepProducts from './steps/StepProducts';
import StepDeliveryReview from './steps/StepDeliveryReview';
import { StyledWizard, StyledWizardBody } from './styles';

const OrderWizard: React.FC<OrderFormProps> = ({
  isEdit,
  loadingSubmit,
  handleSubmit,
}) => {
  const [step, setStep] = useState<WizardStep>(WizardStep.StoreCustomer);
  const isLast = step === WizardStep.DeliveryReview;

  const {
    values: { city, address, products = [], currency },
  } = useFormState<OrderFormValues>();
  const { change, mutators } = useForm();
  const { productId, customerId: customerIdParam } =
    useParams<{ productId: string; customerId: string }>();

  // These queries + effects lived in the old single-page OrderForm. They must
  // stay in this always-mounted orchestrator (NOT inside a step): steps
  // unmount on navigation, which would re-run the prefills on every visit and
  // duplicate the pre-filled product row.
  const { data: fetchedProductData } = useQuery<
    UseQueryResponse<GetProductDetailsResponse>
  >(apiRoutes.PRODUCT_DETAILS(productId || ''), {
    enabled: !!productId,
  });
  const { data: fetchedCustomerData } = useQuery<
    UseQueryResponse<GetCustomerResponse>
  >(apiRoutes.GET_CUSTOMER(customerIdParam || ''), {
    enabled: !!customerIdParam,
  });

  // Also keeps the entity→city / city→deliveryRegion reset effects alive on
  // every step (useFeesProfileData registers them).
  const { currency: preferredCurrency, deliveryFee } = useFeesProfileData();

  const coords = useGeocodeAddress({
    city,
    address,
  });

  useEffect(() => {
    change('latitude', coords.lat);
    change('longitude', coords.lng);
  }, [change, coords]);

  useEffect(() => {
    if (!currency) {
      change('currency', preferredCurrency);
    }
  }, [change, preferredCurrency, currency]);

  useEffect(() => {
    const product = fetchedProductData?.response.product;

    if (productId && product) {
      // sellingPriceIqd/imageUrl are the legacy field names; current
      // responses send sellingPrice/coverImage.
      const price = product.sellingPriceIqd ?? product.sellingPrice ?? 0;

      mutators.push('products', {
        idCol: product.id.toString(),
        imageCol: product.imageUrl ?? product.coverImage ?? '',
        priceCol: price,
        productCol: product.name,
        quantityCol: 1,
        totalCol: {
          currency: 'IQD',
          price,
        },
        sku: product.sku,
      } as ProductsTableItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedProductData, productId]);

  useEffect(() => {
    const customer = fetchedCustomerData?.response;

    if (customerIdParam && customer) {
      change('name', customer.name);
      change('phoneNumber', customer.phone);
      change('address', customer.address);
      change('secondPhoneNumber', customer.secondPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedCustomerData, customerIdParam]);

  // Products pre-fill the total (the amount charged to the customer), but
  // the merchant can override it — e.g. a discount or rounding. The total
  // re-syncs to the products sum only when the PRODUCTS change; a manual
  // edit is never overwritten by this effect.
  const productsSum = useMemo(
    () =>
      (products ?? []).reduce(
        (acc, { priceCol, quantityCol }) => acc + priceCol * quantityCol,
        0
      ),
    [products]
  );

  useEffect(() => {
    if (products && products.length > 0) {
      change('total', productsSum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change, productsSum]);

  useEffect(() => {
    change('deliveryCharge', deliveryFee?.toString() ?? '0');
  }, [change, deliveryFee]);

  // The form can only really submit from the last step (guards against any
  // stray submit event on earlier steps — parity with the old split-form
  // layout where Enter never submitted). On API field errors, jump to the
  // earliest step that owns an errored field.
  const onFormSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    if (!isLast) {
      event.preventDefault();
      return;
    }

    const submission = handleSubmit(event) as unknown as
      | Promise<Record<string, unknown> | undefined>
      | undefined;

    submission?.then?.((submitErrors) => {
      if (!submitErrors) {
        return;
      }

      const errorSteps = Object.keys(submitErrors)
        .map(stepForField)
        .filter((s): s is WizardStep => s !== undefined);

      if (errorSteps.length > 0) {
        setStep(Math.min(...errorSteps) as WizardStep);
      }
    });
  };

  return (
    <StyledWizard noValidate onSubmit={onFormSubmit}>
      <WizardStepper current={step} onJump={setStep} />
      <StyledWizardBody>
        {step === WizardStep.StoreCustomer && <StepStoreCustomer />}
        {step === WizardStep.Products && <StepProducts />}
        {step === WizardStep.DeliveryReview && (
          <StepDeliveryReview isEdit={isEdit} onJump={setStep} />
        )}
      </StyledWizardBody>
      <WizardFooter
        step={step}
        isLast={isLast}
        isEdit={isEdit}
        loading={loadingSubmit}
        onBack={() =>
          setStep((s) => Math.max(s - 1, WizardStep.StoreCustomer) as WizardStep)
        }
        onNext={() =>
          setStep((s) => Math.min(s + 1, WizardStep.DeliveryReview) as WizardStep)
        }
      />
    </StyledWizard>
  );
};

export default OrderWizard;
